import store from '../store';
import userManager from '../utils/userManager';
import {toastr} from 'react-redux-toastr';
import axios from 'axios';

const request = axios.create({
  paramsSerializer: {
    indexes: null
  }
});

request.interceptors.request.use(async (config) => {
  try {
    const user = store.getState().oidc.user;
    if (user && user.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
      return config;
    }
    throw config;
  } catch (error) {
    console.error("Request Error", error);
    return config;
  }
});

request.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      toastr.confirm(
        `It appears your session has timed out or you aren't logged in`,
        {
          timeOut: 0,
          onOk: () => userManager.signinRedirect(),
          okText: "Login",
        }
      );
    }
    return Promise.reject(error);
  }
);

/**
 * @typedef FetchResponse<T = any>
 * @property {T} data
 * 
 * @typedef FetchError<T = any, D = any>
 * @property {string} error
 * @property {string} errorMessage
 * @property {D} errors
 * @property {T} data
 * 
 * @param {string} url 
 * @param {{
 *  method: "GET" | "POST" | "PUT" | "DELETE",
 *  headers: Headers
 * }} options 
 * @param {boolean=} isJson 
 * @returns {Promise<FetchResponse | FetchError>}
 */
function performFetch(url, options, isJson=true) {

  return fetch(url, options)
    .then((res) => {
      if (res.status == 200) {
        if(isJson) {
          return res.json();
        }
        return res;
      } else if (res.status == 401) {
        toastr.confirm(`It appears your session has timed out or you aren't logged in`, {
          timeOut: 0,
          onOk: () => userManager.signinRedirect(),
          okText: 'Login',
        });
      }
      throw res;
    })
    .then((data) => ({data}))
    .catch(async(error) => {
      let errorMessage = isJson ? await error.json() : error; 
      let text = error.statusText ? error.statusText : 'Unknown';
      return {error: text, data: error, errorMessage: errorMessage && errorMessage.message, errors: errorMessage};
    });
}

// a request helper which reads the access_token from the redux state and passes it in its HTTP request
/**
 * 
 * @param {string} url 
 * @param {'GET' | 'POST' | 'PUT' | 'DELETE'=} method 
 * @param {boolean} isJson 
 * @returns
 */
export function apiRequest(url, method = 'GET', isJson = true) {
  const user = store.getState().oidc.user;
  if (user && user.access_token) {
    const token = user.access_token;
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);

    const options = {
      method,
      headers
    };

    return performFetch(url, options, isJson);
  }
  return Promise.resolve({data: {}});
}

export function apiRequestAnonymous(url, method = 'GET', isJson = true, input) {
  const headers = new Headers();
  headers.append('Accept', 'application/json');

  const options = {
    method,
    headers
  };
  if(input){
    options.body = JSON.stringify(input);
    headers.append('Content-Type', 'application/json');
  }

  return performFetch(url, options, isJson);
}


export function apiRequestTimeout(url, timeout = 5000){
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  const headers = new Headers();

  headers.append('Accept', 'application/json');

  const options = {
    method: 'GET',
    headers,
    signal: controller.signal
  };

  return performFetch(url, options); 
}


export function apiPost(url, input, isJson = true) {
  const user = store.getState().oidc.user;
  if (user && user.access_token) {
    const token = user.access_token;
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);
    headers.append('Content-Type', 'application/json');

    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify(input)
    };

    return performFetch(url, options, isJson);
  }
  return Promise.resolve({data: {}});
}

export function apiPut(url, input, isJson = true) {
  const user = store.getState().oidc.user;
  if (user && user.access_token) {
    const token = user.access_token;
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);
    headers.append('Content-Type', 'application/json');

    const options = {
      method: 'PUT',
      headers,
      body: JSON.stringify(input)
    };

    return performFetch(url, options, isJson = true);
  }
  return Promise.resolve({data: {}});
}

export function apiDelete(url, input, isJson = true) {
  const user = store.getState().oidc.user;
  if (user && user.access_token) {
    const token = user.access_token;
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);
    headers.append('Content-Type', 'application/json');

    const options = {
      method: 'DELETE',
      headers,
      body: JSON.stringify(input)
    };

    return performFetch(url, options, isJson);
  }
  return Promise.resolve({data: {}});
}

export {
  request
};